import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Receipt, 
  IndianRupee, 
  Calendar,
  Download,
  Filter,
  CreditCard,
  Banknote,
  Building2,
  PieChart
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXPENSE_CATEGORIES = [
  "Office Supplies", "Utilities", "Rent", "Salaries", "Travel", 
  "Marketing", "Maintenance", "Food & Beverages", "Communication", 
  "Professional Services", "Insurance", "Miscellaneous"
];

const PAYMENT_MODES = ["Cash", "Bank Transfer", "UPI", "Card"];

const categoryColors = {
  'Office Supplies': 'bg-blue-100 text-blue-700',
  'Utilities': 'bg-yellow-100 text-yellow-700',
  'Rent': 'bg-purple-100 text-purple-700',
  'Salaries': 'bg-green-100 text-green-700',
  'Travel': 'bg-indigo-100 text-indigo-700',
  'Marketing': 'bg-pink-100 text-pink-700',
  'Maintenance': 'bg-orange-100 text-orange-700',
  'Food & Beverages': 'bg-red-100 text-red-700',
  'Communication': 'bg-cyan-100 text-cyan-700',
  'Professional Services': 'bg-teal-100 text-teal-700',
  'Insurance': 'bg-emerald-100 text-emerald-700',
  'Miscellaneous': 'bg-slate-100 text-slate-700'
};

const ExpensesPage = () => {
  const { isAdmin } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState('month');
  const _today = new Date();
  const [selectedMonth, setSelectedMonth] = useState(_today.getMonth() + 1);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(_today.getMonth() / 3) + 1);
  const [selectedPeriodYear, setSelectedPeriodYear] = useState(_today.getFullYear());
  const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const periodYears = [_today.getFullYear() - 2, _today.getFullYear() - 1, _today.getFullYear(), _today.getFullYear() + 1];
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [summary, setSummary] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: '',
    description: '',
    amount: '',
    paid_to: '',
    payment_mode: 'Cash',
    receipt_number: '',
    remarks: ''
  });

  // Calculate date range based on period filter
  // NOTE: We build YYYY-MM-DD strings manually to avoid the timezone bug where
  // new Date(y, m, 1).toISOString() converts to UTC, shifting IST dates by ~5.5h
  // and returning the previous day (e.g., 1 May IST -> 30 Apr UTC).
  const ymd = (y, m, d) => `${y}-${String(m).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
  const getDateRange = (period) => {
    const today = new Date();
    let start, end;
    
    switch(period) {
      case 'today':
        start = end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      case 'week': {
        const weekStart = new Date(today);
        weekStart.setDate(today.getDate() - today.getDay());
        start = ymd(weekStart.getFullYear(), weekStart.getMonth() + 1, weekStart.getDate());
        end = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());
        break;
      }
      case 'month': {
        const m = selectedMonth;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, m, 0).getDate();
        start = ymd(y, m, 1);
        end = ymd(y, m, lastDay);
        break;
      }
      case 'quarter': {
        const qStartMonth = (selectedQuarter - 1) * 3 + 1;
        const qEndMonth = qStartMonth + 2;
        const y = selectedPeriodYear;
        const lastDay = new Date(y, qEndMonth, 0).getDate();
        start = ymd(y, qStartMonth, 1);
        end = ymd(y, qEndMonth, lastDay);
        break;
      }
      case 'year':
        start = ymd(selectedPeriodYear, 1, 1);
        end = ymd(selectedPeriodYear, 12, 31);
        break;
      default:
        return { start: '', end: '' };
    }
    return { start, end };
  };

  useEffect(() => {
    if (periodFilter !== 'custom') {
      const range = getDateRange(periodFilter);
      setDateRange(range);
    }
  }, [periodFilter, selectedMonth, selectedQuarter, selectedPeriodYear]);

  useEffect(() => {
    fetchExpenses();
    fetchSummary();
  }, [dateRange]);

  const fetchExpenses = async () => {
    try {
      let url = `${API}/expenses`;
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setExpenses(response.data);
    } catch (error) {
      toast.error('Error fetching expenses');
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      let url = `${API}/expenses/report/summary`;
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary');
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount) || 0
      };
      
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated successfully');
      } else {
        await axios.post(`${API}/expenses`, payload);
        toast.success('Expense added successfully');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving expense');
    }
  };

  const handleEdit = (expense) => {
    setEditingExpense(expense);
    setFormData({
      date: expense.date,
      category: expense.category,
      description: expense.description,
      amount: expense.amount?.toString() || '',
      paid_to: expense.paid_to || '',
      payment_mode: expense.payment_mode || 'Cash',
      receipt_number: expense.receipt_number || '',
      remarks: expense.remarks || ''
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    try {
      await axios.delete(`${API}/expenses/${expenseId}`);
      toast.success('Expense deleted');
      fetchExpenses();
      fetchSummary();
    } catch (error) {
      toast.error('Error deleting expense');
    }
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      category: '',
      description: '',
      amount: '',
      paid_to: '',
      payment_mode: 'Cash',
      receipt_number: '',
      remarks: ''
    });
  };

  const exportToExcel = () => {
    const exportData = filteredExpenses.map(exp => ({
      'Date': exp.date,
      'Category': exp.category,
      'Description': exp.description,
      'Amount (₹)': exp.amount,
      'Paid To': exp.paid_to || '-',
      'Payment Mode': exp.payment_mode,
      'Receipt No.': exp.receipt_number || '-',
      'Remarks': exp.remarks || '-'
    }));

    // Add summary row
    exportData.push({
      'Date': '',
      'Category': 'TOTAL',
      'Description': '',
      'Amount (₹)': filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0),
      'Paid To': '',
      'Payment Mode': '',
      'Receipt No.': '',
      'Remarks': ''
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Expenses');
    
    const fileName = `Expenses_Report_${dateRange.start || 'All'}_to_${dateRange.end || 'All'}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success('Report exported successfully');
  };

  const formatCurrency = (amount) => `₹${(amount || 0).toLocaleString('en-IN')}`;

  const filteredExpenses = expenses.filter(expense => {
    const matchesSearch = expense.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      expense.paid_to?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || expense.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Calculate totals
  const totalAmount = filteredExpenses.reduce((sum, e) => sum + (e.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="expenses-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-brand-navy font-heading">Expenses</h1>
          <p className="text-slate-500 mt-1">Track daily office expenses</p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={exportToExcel}
            data-testid="export-expenses-btn"
            className="border-brand-navy text-brand-navy hover:bg-brand-navy/5"
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            data-testid="add-expense-btn"
            className="bg-brand-navy text-white hover:bg-brand-navy/90 shadow-lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Expense
          </Button>
        </div>
      </div>

      {/* Period Filter Tabs */}
      <div className="flex flex-wrap items-center gap-2 border-b pb-2">
        {[
          { key: 'today', label: 'Today' },
          { key: 'week', label: 'This Week' },
          { key: 'month', label: 'This Month' },
          { key: 'quarter', label: 'This Quarter' },
          { key: 'year', label: 'This Year' },
          { key: 'custom', label: 'Custom' }
        ].map(tab => (
          <Button
            key={tab.key}
            variant={periodFilter === tab.key ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setPeriodFilter(tab.key)}
            className={periodFilter === tab.key ? 'bg-brand-navy text-white' : ''}
            data-testid={`expenses-period-${tab.key}`}
          >
            {tab.label}
          </Button>
        ))}
        {periodFilter === 'month' && (
          <>
            <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
              <SelectTrigger className="w-[140px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{monthNames.map((n, i) => <SelectItem key={i+1} value={(i+1).toString()}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'quarter' && (
          <>
            <Select value={selectedQuarter.toString()} onValueChange={(v) => setSelectedQuarter(parseInt(v))}>
              <SelectTrigger className="w-[170px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
              </SelectContent>
            </Select>
            <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
              <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </>
        )}
        {periodFilter === 'year' && (
          <Select value={selectedPeriodYear.toString()} onValueChange={(v) => setSelectedPeriodYear(parseInt(v))}>
            <SelectTrigger className="w-[100px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{periodYears.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-brand-navy to-brand-navy/90">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-white" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{formatCurrency(totalAmount)}</p>
                <p className="text-xs text-white/70">Total Expenses</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Banknote className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(summary?.by_payment_mode?.Cash || 0)}
                </p>
                <p className="text-xs text-slate-500">Cash Payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">
                  {formatCurrency((summary?.by_payment_mode?.['Bank Transfer'] || 0) + (summary?.by_payment_mode?.UPI || 0))}
                </p>
                <p className="text-xs text-slate-500">Digital Payments</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Receipt className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-600">{filteredExpenses.length}</p>
                <p className="text-xs text-slate-500">Total Entries</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by description or paid to..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  data-testid="search-expenses"
                  className="pl-10"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">From Date</Label>
              <Input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                className="w-[150px]"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-slate-500">To Date</Label>
              <Input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                className="w-[150px]"
              />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {EXPENSE_CATEGORIES.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold text-slate-600">Date</TableHead>
                <TableHead className="font-semibold text-slate-600">Category</TableHead>
                <TableHead className="font-semibold text-slate-600">Description</TableHead>
                <TableHead className="font-semibold text-slate-600">Paid To</TableHead>
                <TableHead className="font-semibold text-slate-600">Amount</TableHead>
                <TableHead className="font-semibold text-slate-600">Mode</TableHead>
                <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredExpenses.map((expense) => (
                <TableRow key={expense.id} className="hover:bg-slate-50/50">
                  <TableCell className="font-medium">
                    {new Date(expense.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </TableCell>
                  <TableCell>
                    <Badge className={`${categoryColors[expense.category] || 'bg-slate-100'} border-0`}>
                      {expense.category}
                    </Badge>
                  </TableCell>
                  <TableCell className="max-w-[200px] truncate">{expense.description}</TableCell>
                  <TableCell>{expense.paid_to || '-'}</TableCell>
                  <TableCell className="font-semibold text-brand-navy">
                    {formatCurrency(expense.amount)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {expense.payment_mode}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => handleEdit(expense)}
                        data-testid={`edit-expense-${expense.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-red-500 hover:text-red-700"
                          onClick={() => handleDelete(expense.id)}
                          data-testid={`delete-expense-${expense.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredExpenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-400">
                    <Receipt className="h-12 w-12 mx-auto mb-2 text-slate-300" />
                    No expenses found
                  </TableCell>
                </TableRow>
              )}
              {filteredExpenses.length > 0 && (
                <TableRow className="bg-slate-50 font-semibold">
                  <TableCell colSpan={4} className="text-right">Total:</TableCell>
                  <TableCell className="text-brand-navy text-lg">{formatCurrency(totalAmount)}</TableCell>
                  <TableCell colSpan={2}></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-brand-navy">
              {editingExpense ? 'Edit Expense' : 'Add New Expense'}
            </DialogTitle>
            <DialogDescription>
              {editingExpense ? 'Update expense details' : 'Enter expense details'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  data-testid="input-expense-date"
                />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger data-testid="select-expense-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                data-testid="input-expense-description"
                placeholder="What was this expense for?"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  data-testid="input-expense-amount"
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>Payment Mode</Label>
                <Select
                  value={formData.payment_mode}
                  onValueChange={(v) => setFormData(prev => ({ ...prev, payment_mode: v }))}
                >
                  <SelectTrigger data-testid="select-payment-mode">
                    <SelectValue placeholder="Select mode" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map(mode => (
                      <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Paid To</Label>
                <Input
                  value={formData.paid_to}
                  onChange={(e) => setFormData(prev => ({ ...prev, paid_to: e.target.value }))}
                  data-testid="input-paid-to"
                  placeholder="Vendor/Person name"
                />
              </div>
              <div className="space-y-2">
                <Label>Receipt No.</Label>
                <Input
                  value={formData.receipt_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, receipt_number: e.target.value }))}
                  data-testid="input-receipt-number"
                  placeholder="Optional"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Input
                value={formData.remarks}
                onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                data-testid="input-expense-remarks"
                placeholder="Any additional notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              data-testid="submit-expense-btn"
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              disabled={!formData.date || !formData.category || !formData.description || !formData.amount}
            >
              {editingExpense ? 'Update Expense' : 'Add Expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpensesPage;
